import { Box, Chip, MenuItem, TableCell, TableRow, Typography } from '@mui/material';
import { capitalCase } from 'change-case';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { WrappedAppointment } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Appointment';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import Iconify from 'src/components/Iconify';
import { TableMoreMenu } from 'src/components/table';
import { AppointmentParticipant } from 'src/careflow/ph-core/fhir-client/resources';
import { formatDateTimeToDefault } from 'src/utils/dates';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import { AppointmentForm } from './AppointmentForm';
import useComponentsConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useComponentsConfigData';

type Props = {
  row: WrappedAppointment | null;
  patient: WrappedPatient | null;
  handleAppointment: (data: any) => Promise<any>;
};

export function Row({ row, patient, handleAppointment }: Props) {
  const [data, setData] = useState(null);
  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);
  const [openModalEdit, setOpenModalEdit] = useState(false);

  const componentsConfigData = useComponentsConfigData();

  const contextConfig = useMemo(() => {
    if (!componentsConfigData) {
      return;
    }

    // CHECK HERE the context of the form if needed to switch the config data

    return componentsConfigData.appointmentListConfig;
  }, [componentsConfigData]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };

  const handleEdit = (row: any) => {
    handleCloseMenu();
    setData(row);
    setOpenModalEdit(true);
  };

  return (
    <>
      <TableRow>
        <TableCell>{row?.status && capitalCase(row?.status)}</TableCell>
        {!contextConfig?.reasonColumn?.hide && (
          <TableCell>
            {row?.reasonCode?.[0]?.coding?.[0]?.display &&
              row?.reasonCode?.[0]?.coding?.[0]?.display}
          </TableCell>
        )}

        {!contextConfig?.reasonTextColumn?.hide && (
          <TableCell>{row?.reasonCode?.[0]?.text && row?.reasonCode?.[0]?.text}</TableCell>
        )}
        <TableCell>
          <Box sx={{ maxWidth: 300 }}>
            <Typography style={{ cursor: 'pointer' }} noWrap sx={{ fontSize: `0.75rem` }}>
              {row?.description}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>{formatDateTimeToDefault(row?.start)}</TableCell>
        <TableCell>{formatDateTimeToDefault(row?.end)}</TableCell>
        <TableCell>
          <Box sx={{ maxWidth: 200 }}>
            {row?.participant
              ?.filter(
                (e: AppointmentParticipant) =>
                  patient?.id && !e.actor?.reference?.includes(patient?.id)
              )
              .map((e: AppointmentParticipant, index: number) => (
                <Chip
                  clickable={false}
                  size="small"
                  key={index}
                  label={e?.actor?.display}
                  sx={{ m: 0.2 }}
                />
              ))}
          </Box>
        </TableCell>
        <TableCell>{row?.created && moment(row?.created).format('MM/DD/YYYY')}</TableCell>
        <TableCell align="right">
          <TableMoreMenu
            open={openMenu}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            actions={
              checkAclValidation({ acls: [crsAcls.CRS.PATIENT.APPOINTMENT.EDIT] })
                ? [
                    <MenuItem key={'menu-i-edit-'} onClick={() => handleEdit(row)}>
                      <Iconify icon={'mdi:pencil'} />
                      Edit
                    </MenuItem>,
                  ]
                : null
            }
          />
        </TableCell>
      </TableRow>
      <AppointmentForm
        patient={patient}
        appointment={data}
        open={openModalEdit}
        onClose={() => setOpenModalEdit(false)}
        handleAppointment={handleAppointment}
      />
    </>
  );
}
